/**
 * Swamithra — Expert panel authentication (US-01-005).
 *
 * THE OPPOSITE OF ../api/auth ON ONE POINT. The user app tells the entry screen
 * whether a number is registered, because it has to decide whether to show the
 * profile fields. The panel must NOT: an unknown number and a deactivated
 * Expert both get the same generic `not_authorised` failure and no WhatsApp
 * message of any kind is dispatched (US-01-005.6/.7). There is therefore no
 * lookup function here, and adding one would be an account-enumeration hole.
 *
 * There is also no registration function. The panel has no self-sign-up route
 * and a POST of an expert record without a super_admin session is a 403
 * (US-01-005.13) — see ./panel-experts, where provisioning lives.
 */

import { z } from "zod"

import { panelIdentitySchema, panelOtpChallengeSchema } from "../domain/expert"
import { mobileNumberSchema } from "../domain/phone"
import { otpCodeSchema, sessionSchema } from "../domain/user"
import { revokeSession } from "./auth"
import { api } from "./client"
import { type ReadOptions } from "./options"

const panelOtpRequestSchema = z.object({ mobileNumber: mobileNumberSchema })
const panelOtpVerifySchema = z.object({
	mobileNumber: mobileNumberSchema,
	code: otpCodeSchema,
})

/**
 * Requests a login code for a provisioned, active Expert.
 *
 * Fails with `not_authorised` for everything else, and with `otp_locked` — plus
 * a `Retry-After` — for a number inside its 30-minute lockout, from any browser
 * or device (US-01-005.9).
 */
export async function requestPanelOtp(mobileNumber: string, options: ReadOptions = {}) {
	return await api.post(
		"/expert/auth/otp",
		panelOtpChallengeSchema,
		panelOtpRequestSchema.parse({ mobileNumber }),
		{ ...options, anonymous: true },
	)
}

/**
 * Verifies the code and opens the panel session.
 *
 * No token comes back in the body: the panel is web-only and the JWT is carried
 * ONLY in an httpOnly, Secure, SameSite=Lax cookie the JavaScript never sees
 * (US-01-005.12).
 */
export async function verifyPanelOtp(
	mobileNumber: string,
	code: string,
	options: ReadOptions = {},
) {
	// The SAME session shape the user app's verification answers with — one
	// endpoint per audience, one body — with `identity.kind` saying which and
	// `identity.role` carrying `expert` or `super_admin`.
	return await api.post(
		"/expert/auth/otp/verify",
		sessionSchema,
		panelOtpVerifySchema.parse({ mobileNumber, code }),
		{ ...options, anonymous: true },
	)
}

/**
 * The current panel session, and the point at which its idle window slides.
 *
 * `isActive` is re-checked here and on every other authenticated route, not only
 * at OTP issue — which is what makes a Super Admin's deactivation take effect on
 * the Expert's very next call (US-01-005.17).
 */
export function fetchPanelSession(options: ReadOptions = {}) {
	return api.get("/expert/me", panelIdentitySchema, options)
}

/**
 * Clears the cookie and invalidates the session server-side (US-01-005.23).
 *
 * The panel is cookie-only, so a sign-out that did not reach the server leaves a
 * cookie that still works — which is why the same replayed cookie being refused
 * afterwards is the criterion, not the button clearing the screen.
 */
export function panelSignOut(options: ReadOptions = {}): Promise<void> {
	return revokeSession("/expert/auth/sign-out", options)
}
